(function () {
  'use strict';

  /*
   * ==============================================================
   * BZ CART COMPLETION ENGINE
   * ==============================================================
   *
   * PRIMARY RESPONSIBILITIES
   * - Read the current Shopify cart
   * - Rebuild cart state after every cart update
   * - Identify categories currently represented in the cart
   * - Block recommendations from represented categories
   * - Rank eligible candidates primarily by free-shipping price fit
   * - Use outfit-completion value as a secondary signal
   * - Prevent duplicate products
   * - Preserve category variety
   * - Clone only the selected Horizon-native product cards
   *
   * LIQUID OWNS
   * - Candidate rendering
   * - Product images
   * - Product links
   * - Horizon-native product forms
   *
   * JAVASCRIPT OWNS
   * - Cart analysis
   * - Category classification
   * - Candidate filtering
   * - Recommendation scoring
   * - Recommendation selection
   * - Live recommendation refreshes
   *
   * IMPORTANT
   * - This file does not submit products to the cart
   * - This file does not replace Horizon product forms
   * - This is progressive enhancement
   */

  const ROOT_SELECTOR = '[data-bz-cart-upsell-root]';
  const RAIL_SELECTOR = '[data-bz-upsell-rail]';
  const LIBRARY_SELECTOR =
    'template[data-bz-upsell-candidate-library]';
  const CANDIDATE_SELECTOR = '[data-bz-candidate-card]';
  const WRAPPER_SELECTOR = '.bz-cart__upsell';

  const MAX_RECOMMENDATIONS = 12;
  const INITIAL_CATEGORY_LIMIT = 3;

  /*
   * Categories used throughout the engine.
   *
   * Both Ready to Wear Bangle Sets and Accent Boxes are assigned
   * the normalized "bangles" category by Liquid.
   *
   * BYOB products are not recommendation candidates, but when a
   * BYOB product is found in the cart it is also classified as
   * "bangles", preventing all bangle recommendations.
   */
  const CATEGORY = {
    NECKLACE: 'necklace',
    EARRINGS: 'earrings',
    TIKKA: 'tikka',
    RING: 'ring',
    NOSE_RING: 'nose-ring',
    BANGLES: 'bangles',
    OTHER: 'other',
  };

  /*
   * Small merchandising baseline.
   *
   * Free-shipping price fit remains the dominant signal. These
   * values only help resolve products with similar price fit.
   */
  const CATEGORY_BASE_SCORE = {
    [CATEGORY.EARRINGS]: 14,
    [CATEGORY.BANGLES]: 13,
    [CATEGORY.TIKKA]: 12,
    [CATEGORY.RING]: 11,
    [CATEGORY.NECKLACE]: 9,
    [CATEGORY.NOSE_RING]: 6,
    [CATEGORY.OTHER]: 0,
  };

  let activeRequestController = null;
  let requestSequence = 0;
  let enhancementTimer = null;

  function getConfig() {
    const element = document.getElementById(
      'bz-shipping-config'
    );

    if (!element) {
      return null;
    }

    try {
      return JSON.parse(element.textContent);
    } catch {
      return null;
    }
  }

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[_–—-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  /*
   * Classifies products currently in the cart.
   *
   * The candidate library already exposes normalized categories
   * through data-category, so this function is mainly required for
   * /cart.js items.
   */
  function classifyCartItem(item) {
    const combinedText = normalizeText([
      item?.product_type,
      item?.handle,
      item?.product_title,
      item?.title,
    ].join(' '));

    if (!combinedText) {
      return CATEGORY.OTHER;
    }

    /*
     * BYOB is treated as bangles.
     *
     * These checks appear before other category checks to ensure a
     * Build Your Own Bangle Box cannot be misclassified.
     */
    if (
      combinedText.includes('build your own bangle') ||
      combinedText.includes('build your own box') ||
      combinedText.includes('bangle box') ||
      combinedText.includes('byob')
    ) {
      return CATEGORY.BANGLES;
    }

    if (
      combinedText.includes('accent box') ||
      combinedText.includes('bangle') ||
      combinedText.includes('bracelet')
    ) {
      return CATEGORY.BANGLES;
    }

    if (
      combinedText.includes('nose ring') ||
      combinedText.includes('nose pin') ||
      combinedText.includes('nath')
    ) {
      return CATEGORY.NOSE_RING;
    }

    if (
      combinedText.includes('necklace') ||
      combinedText.includes('haar')
    ) {
      return CATEGORY.NECKLACE;
    }

    if (
      combinedText.includes('earring') ||
      combinedText.includes('jhumka')
    ) {
      return CATEGORY.EARRINGS;
    }

    if (
      combinedText.includes('tikka') ||
      combinedText.includes('maang tikka')
    ) {
      return CATEGORY.TIKKA;
    }

    if (combinedText.includes('ring')) {
      return CATEGORY.RING;
    }

    return CATEGORY.OTHER;
  }

  function normalizeCandidateCategory(value) {
    const category = normalizeText(value);

    switch (category) {
      case CATEGORY.NECKLACE:
      case CATEGORY.EARRINGS:
      case CATEGORY.TIKKA:
      case CATEGORY.RING:
      case CATEGORY.NOSE_RING:
      case CATEGORY.BANGLES:
        return category;

      default:
        return CATEGORY.OTHER;
    }
  }

  /*
   * Rebuilds the complete cart state from the newest /cart.js
   * response.
   *
   * No previous category state is retained.
   *
   * Therefore, when an item is removed, its category automatically
   * becomes eligible again on the next refresh.
   */
  function buildCartState(cart, thresholdCents) {
    const productIds = new Set();
    const variantIds = new Set();
    const representedCategories = new Set();

    cart.items.forEach((item) => {
      if (item?.product_id != null) {
        productIds.add(String(item.product_id));
      }

      if (item?.variant_id != null) {
        variantIds.add(String(item.variant_id));
      }

      const category = classifyCartItem(item);

      if (category !== CATEGORY.OTHER) {
        representedCategories.add(category);
      }
    });

    const subtotal = Number(
      cart.items_subtotal_price
    );

    const safeSubtotal = Number.isFinite(subtotal)
      ? subtotal
      : 0;

    const remaining = Math.max(
      thresholdCents - safeSubtotal,
      0
    );

    return {
      productIds,
      variantIds,
      representedCategories,
      subtotal: safeSubtotal,
      remaining,
    };
  }

  function updateVisibility(subtotal) {
    const config = getConfig();

    if (
      !config ||
      typeof config.thresholdCents !== 'number'
    ) {
      return;
    }

    const remaining = Math.max(
      config.thresholdCents - subtotal,
      0
    );

    document
      .querySelectorAll(WRAPPER_SELECTOR)
      .forEach((wrapper) => {
        const hasNoCandidates =
          wrapper.dataset.bzNoCandidates === 'true';

        if (remaining <= 0 || hasNoCandidates) {
          wrapper.style.display = 'none';
        } else {
          wrapper.style.display = '';
        }
      });
  }

  /*
   * ==============================================================
   * SHIPPING-GAP SCORING
   * ==============================================================
   *
   * This is the strongest recommendation signal.
   *
   * Products that meet or slightly exceed the remaining free-
   * shipping amount receive the highest score.
   *
   * Products slightly below the gap remain useful, but score lower
   * because they do not unlock free shipping by themselves.
   *
   * Products that cause excessive overspending are progressively
   * penalized.
   */
  function scorePriceFit(productPrice, remaining) {
    if (
      !Number.isFinite(productPrice) ||
      productPrice <= 0 ||
      !Number.isFinite(remaining) ||
      remaining <= 0
    ) {
      return 0;
    }

    const difference = productPrice - remaining;
    const differenceRatio =
      Math.abs(difference) / remaining;

    /*
     * Product reaches the threshold.
     */
    if (difference >= 0) {
      /*
       * Exact match or up to 10% over:
       * strongest possible recommendations.
       */
      if (differenceRatio <= 0.1) {
        return 120 - differenceRatio * 100;
      }

      /*
       * Between 10% and 35% over:
       * still a strong and reasonable unlock.
       */
      if (differenceRatio <= 0.35) {
        return 110 -
          ((differenceRatio - 0.1) / 0.25) * 30;
      }

      /*
       * Between 35% and 75% over:
       * useful, but increasingly expensive relative to the gap.
       */
      if (differenceRatio <= 0.75) {
        return 80 -
          ((differenceRatio - 0.35) / 0.4) * 40;
      }

      /*
       * More than 75% over:
       * remains eligible but receives a low shipping-fit score.
       */
      return Math.max(
        8,
        40 - (differenceRatio - 0.75) * 22
      );
    }

    /*
     * Product falls below the threshold.
     */
    if (differenceRatio <= 0.1) {
      /*
       * Up to 10% below:
       * very close, but does not independently unlock shipping.
       */
      return 106 - differenceRatio * 110;
    }

    if (differenceRatio <= 0.25) {
      /*
       * Between 10% and 25% below.
       */
      return 95 -
        ((differenceRatio - 0.1) / 0.15) * 30;
    }

    if (differenceRatio <= 0.5) {
      /*
       * Between 25% and 50% below.
       */
      return 65 -
        ((differenceRatio - 0.25) / 0.25) * 38;
    }

    /*
     * More than 50% below:
     * weak fit because another item would still be required.
     */
    return Math.max(
      5,
      27 - (differenceRatio - 0.5) * 30
    );
  }

  /*
   * ==============================================================
   * COMPLETION SCORING
   * ==============================================================
   *
   * Completion value is intentionally secondary to shipping-gap
   * fit.
   *
   * This function uses explicit cart-state rules rather than a large
   * category-priority table.
   */
  function scoreCompletionFit(
    candidateCategory,
    representedCategories
  ) {
    let score =
      CATEGORY_BASE_SCORE[candidateCategory] || 0;

    const hasNecklace =
      representedCategories.has(CATEGORY.NECKLACE);

    const hasEarrings =
      representedCategories.has(CATEGORY.EARRINGS);

    const hasTikka =
      representedCategories.has(CATEGORY.TIKKA);

    const hasRing =
      representedCategories.has(CATEGORY.RING);

    const hasNoseRing =
      representedCategories.has(CATEGORY.NOSE_RING);

    const hasBangles =
      representedCategories.has(CATEGORY.BANGLES);

    /*
     * Bangles in cart:
     * prioritize complementary upper-body and finishing pieces.
     */
    if (hasBangles) {
      if (candidateCategory === CATEGORY.EARRINGS) {
        score += 18;
      }

      if (candidateCategory === CATEGORY.NECKLACE) {
        score += 16;
      }

      if (candidateCategory === CATEGORY.TIKKA) {
        score += 14;
      }

      if (candidateCategory === CATEGORY.RING) {
        score += 10;
      }

      if (candidateCategory === CATEGORY.NOSE_RING) {
        score += 5;
      }
    }

    /*
     * Earrings in cart:
     * prioritize necklace, bangles, and tikka.
     */
    if (hasEarrings) {
      if (candidateCategory === CATEGORY.NECKLACE) {
        score += 17;
      }

      if (candidateCategory === CATEGORY.BANGLES) {
        score += 15;
      }

      if (candidateCategory === CATEGORY.TIKKA) {
        score += 13;
      }

      if (candidateCategory === CATEGORY.RING) {
        score += 8;
      }

      if (candidateCategory === CATEGORY.NOSE_RING) {
        score += 4;
      }
    }

    /*
     * Necklace in cart:
     * prioritize earrings, bangles, and tikka.
     */
    if (hasNecklace) {
      if (candidateCategory === CATEGORY.EARRINGS) {
        score += 17;
      }

      if (candidateCategory === CATEGORY.BANGLES) {
        score += 15;
      }

      if (candidateCategory === CATEGORY.TIKKA) {
        score += 13;
      }

      if (candidateCategory === CATEGORY.RING) {
        score += 8;
      }

      if (candidateCategory === CATEGORY.NOSE_RING) {
        score += 4;
      }
    }

    /*
     * Tikka in cart:
     * prioritize earrings, necklace, and bangles.
     */
    if (hasTikka) {
      if (candidateCategory === CATEGORY.EARRINGS) {
        score += 15;
      }

      if (candidateCategory === CATEGORY.NECKLACE) {
        score += 14;
      }

      if (candidateCategory === CATEGORY.BANGLES) {
        score += 12;
      }

      if (candidateCategory === CATEGORY.RING) {
        score += 7;
      }
    }

    /*
     * Ring in cart:
     * prioritize bangles, earrings, and necklace.
     */
    if (hasRing) {
      if (candidateCategory === CATEGORY.BANGLES) {
        score += 14;
      }

      if (candidateCategory === CATEGORY.EARRINGS) {
        score += 12;
      }

      if (candidateCategory === CATEGORY.NECKLACE) {
        score += 10;
      }

      if (candidateCategory === CATEGORY.TIKKA) {
        score += 7;
      }
    }

    /*
     * Nose ring in cart:
     * prioritize tikka, necklace, and earrings.
     */
    if (hasNoseRing) {
      if (candidateCategory === CATEGORY.TIKKA) {
        score += 12;
      }

      if (candidateCategory === CATEGORY.NECKLACE) {
        score += 11;
      }

      if (candidateCategory === CATEGORY.EARRINGS) {
        score += 10;
      }

      if (candidateCategory === CATEGORY.BANGLES) {
        score += 7;
      }
    }

    /*
     * Prevent multiple cart items from causing completion bonuses to
     * overpower the shipping-gap score.
     */
    return Math.min(score, 34);
  }

  function getCandidateMetadata(card) {
    const productId = String(
      card.dataset.productId || ''
    );

    const variantId = String(
      card.dataset.variantId || ''
    );

    const productPrice = Number(
      card.dataset.productPrice
    );

    const category = normalizeCandidateCategory(
      card.dataset.category
    );

    const sourceCollection = String(
      card.dataset.sourceCollection || ''
    );

    const collectionPosition = Number(
      card.dataset.collectionPosition
    );

    const categoryPosition = Number(
      card.dataset.categoryPosition
    );

    return {
      card,
      productId,
      variantId,
      productPrice,
      category,
      sourceCollection,
      collectionPosition:
        Number.isFinite(collectionPosition)
          ? collectionPosition
          : Number.MAX_SAFE_INTEGER,
      categoryPosition:
        Number.isFinite(categoryPosition)
          ? categoryPosition
          : Number.MAX_SAFE_INTEGER,
    };
  }

  function collectEligibleCandidates(
    library,
    cartState
  ) {
    const libraryCards = Array.from(
      library.content.querySelectorAll(
        CANDIDATE_SELECTOR
      )
    );

    const seenProductIds = new Set();
    const seenVariantIds = new Set();
    const eligibleCandidates = [];

    libraryCards.forEach((card) => {
      const candidate = getCandidateMetadata(card);

      if (!candidate.productId) {
        return;
      }

      /*
       * Do not recommend an exact product already in the cart.
       */
      if (
        cartState.productIds.has(candidate.productId)
      ) {
        return;
      }

      /*
       * Also exclude exact variants already represented.
       */
      if (
        candidate.variantId &&
        cartState.variantIds.has(candidate.variantId)
      ) {
        return;
      }

      /*
       * Block every category currently represented in the cart.
       *
       * Examples:
       * - Earrings in cart → no earrings
       * - Ring in cart → no rings
       * - BYOB in cart → no Ready to Wear or Accent Boxes
       */
      if (
        cartState.representedCategories.has(
          candidate.category
        )
      ) {
        return;
      }

      /*
       * Products may belong to more than one source collection.
       * Keep only one recommendation candidate per product.
       */
      if (seenProductIds.has(candidate.productId)) {
        return;
      }

      if (
        candidate.variantId &&
        seenVariantIds.has(candidate.variantId)
      ) {
        return;
      }

      seenProductIds.add(candidate.productId);

      if (candidate.variantId) {
        seenVariantIds.add(candidate.variantId);
      }

      eligibleCandidates.push(candidate);
    });

    return eligibleCandidates;
  }

  function scoreCandidates(
    candidates,
    cartState
  ) {
    return candidates
      .map((candidate) => {
        const priceScore = scorePriceFit(
          candidate.productPrice,
          cartState.remaining
        );

        const completionScore =
          scoreCompletionFit(
            candidate.category,
            cartState.representedCategories
          );

        return {
          ...candidate,
          priceScore,
          completionScore,
          totalScore:
            priceScore + completionScore,
        };
      })
      .sort((a, b) => {
        if (b.totalScore !== a.totalScore) {
          return b.totalScore - a.totalScore;
        }

        /*
         * When scores match, prefer the product with the stronger
         * shipping-gap fit.
         */
        if (b.priceScore !== a.priceScore) {
          return b.priceScore - a.priceScore;
        }

        /*
         * Preserve the collection sequence defined by Liquid.
         */
        if (
          a.categoryPosition !== b.categoryPosition
        ) {
          return (
            a.categoryPosition -
            b.categoryPosition
          );
        }

        /*
         * Preserve Shopify collection merchandising order as the
         * final tie-breaker.
         */
        return (
          a.collectionPosition -
          b.collectionPosition
        );
      });
  }

  /*
   * Selects recommendations in two passes.
   *
   * Pass 1:
   * Maximum three products from one category to encourage variety.
   *
   * Pass 2:
   * If fewer than twelve recommendations were selected, relax the
   * category limit and fill the remaining spaces with the strongest
   * eligible products.
   */
  function selectRecommendations(
    scoredCandidates
  ) {
    const selected = [];
    const selectedProductIds = new Set();
    const categoryCounts = new Map();

    scoredCandidates.forEach((candidate) => {
      if (
        selected.length >= MAX_RECOMMENDATIONS
      ) {
        return;
      }

      const currentCategoryCount =
        categoryCounts.get(candidate.category) || 0;

      if (
        currentCategoryCount >=
        INITIAL_CATEGORY_LIMIT
      ) {
        return;
      }

      selected.push(candidate);
      selectedProductIds.add(candidate.productId);

      categoryCounts.set(
        candidate.category,
        currentCategoryCount + 1
      );
    });

    /*
     * Relax the category cap only when needed to reach twelve.
     */
    if (selected.length < MAX_RECOMMENDATIONS) {
      scoredCandidates.forEach((candidate) => {
        if (
          selected.length >= MAX_RECOMMENDATIONS
        ) {
          return;
        }

        if (
          selectedProductIds.has(candidate.productId)
        ) {
          return;
        }

        selected.push(candidate);
        selectedProductIds.add(candidate.productId);
      });
    }

    return selected;
  }

  function renderRecommendations(
    root,
    recommendations
  ) {
    const rail = root.querySelector(RAIL_SELECTOR);

    if (!rail) {
      return false;
    }

    /*
     * Remove the Liquid fallback only after recommendations have
     * been successfully calculated.
     */
    rail.replaceChildren();

    recommendations.forEach((candidate) => {
      const clonedCard =
        candidate.card.cloneNode(true);

      clonedCard.removeAttribute(
        'data-bz-candidate-card'
      );

      clonedCard.setAttribute(
        'data-bz-rendered-recommendation',
        ''
      );

      clonedCard.dataset.bzPriceScore =
        candidate.priceScore.toFixed(2);

      clonedCard.dataset.bzCompletionScore =
        candidate.completionScore.toFixed(2);

      clonedCard.dataset.bzTotalScore =
        candidate.totalScore.toFixed(2);

      rail.appendChild(clonedCard);
    });

    root.dataset.bzIntelligenceApplied = 'true';

    const wrapper = root.closest(
      WRAPPER_SELECTOR
    );

    if (wrapper) {
      wrapper.dataset.bzNoCandidates =
        recommendations.length === 0
          ? 'true'
          : 'false';
    }

    return recommendations.length > 0;
  }

  function enhanceUpsellRoot(root, cartState) {
    const library = root.querySelector(
      LIBRARY_SELECTOR
    );

    if (!library) {
      return false;
    }

    const eligibleCandidates =
      collectEligibleCandidates(
        library,
        cartState
      );

    const scoredCandidates = scoreCandidates(
      eligibleCandidates,
      cartState
    );

    const recommendations =
      selectRecommendations(scoredCandidates);

    return renderRecommendations(
      root,
      recommendations
    );
  }

  function applyIntelligence(cart) {
    const config = getConfig();

    if (
      !config ||
      typeof config.thresholdCents !== 'number' ||
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return;
    }

    const cartState = buildCartState(
      cart,
      config.thresholdCents
    );

    updateVisibility(cartState.subtotal);

    /*
     * Once free shipping is unlocked, the upsell wrapper remains
     * hidden and no recommendation work is necessary.
     */
    if (cartState.remaining <= 0) {
      return;
    }

    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        enhanceUpsellRoot(root, cartState);
      });

    /*
     * Run visibility again because category and duplicate filtering
     * may have produced no eligible candidates.
     */
    updateVisibility(cartState.subtotal);
  }

  async function fetchCartAndEnhance() {
    const currentSequence = ++requestSequence;

    if (activeRequestController) {
      activeRequestController.abort();
    }

    activeRequestController =
      new AbortController();

    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        signal: activeRequestController.signal,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return;
      }

      const cart = await response.json();

      /*
       * Ignore stale cart responses when a newer request has already
       * started.
       */
      if (currentSequence !== requestSequence) {
        return;
      }

      applyIntelligence(cart);
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }

      /*
       * Progressive enhancement:
       * if cart retrieval fails, leave the Liquid-rendered fallback
       * untouched.
       */
    }
  }

  function scheduleEnhancement() {
    window.clearTimeout(enhancementTimer);

    enhancementTimer = window.setTimeout(() => {
      fetchCartAndEnhance();
    }, 60);
  }

  function handleShippingUpdated(event) {
    const subtotal = event.detail?.subtotal;

    if (typeof subtotal === 'number') {
      /*
       * Hide or reveal the wrapper immediately using the subtotal
       * already calculated by the shipping engine.
       */
      updateVisibility(subtotal);
    }

    /*
     * Fetch the full cart required for category blocking and
     * recommendation rebuilding.
     */
    scheduleEnhancement();
  }

  function init() {
    document.addEventListener(
      'bz:shipping:updated',
      handleShippingUpdated
    );

    /*
     * Initial run in case the shipping event occurred before this
     * script was initialized.
     */
    scheduleEnhancement();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();